import { METRIC_COLORS } from "@/lib/designTokens";

const ROLE_COLOR = {
  warmup: METRIC_COLORS.speed,
  work: METRIC_COLORS.power,
  recovery: METRIC_COLORS.elevation,
  cooldown: METRIC_COLORS.neutral,
};

const ROLE_HEIGHT = {
  warmup: 42,
  work: 88,
  recovery: 28,
  cooldown: 34,
};

const roleFor = (stepType) => {
  const type = String(stepType || "").toUpperCase();
  if (type === "WARMUP") return "warmup";
  if (type === "COOLDOWN") return "cooldown";
  if (type === "WORK" || type === "DRILL" || type === "SKILL") return "work";
  return "recovery";
};

const numericHeight = (intensity, sport) => {
  if (!intensity) return null;
  const unit = String(intensity.unit || "").toUpperCase();
  const method = String(intensity.method || "").toUpperCase();
  const lower = intensity.lower;
  const upper = intensity.upper;
  if (lower == null && upper == null) return null;
  const mid = lower != null && upper != null ? (Number(lower) + Number(upper)) / 2 : Number(lower ?? upper);
  if (!Number.isFinite(mid)) return null;
  if (unit === "FTP_PERCENT" || method === "POWER_FTP_PCT" || method === "POWER_CP") {
    return Math.max(12, Math.min(100, mid));
  }
  if (unit === "RPE_0_10" || method === "RPE") {
    return Math.max(12, Math.min(100, mid * 10));
  }
  if (unit === "HR_ZONE") {
    return Math.max(12, Math.min(100, mid * 18));
  }
  if (unit === "HR_PERCENT") {
    return Math.max(12, Math.min(100, mid));
  }
  if (sport === "running" && unit.includes("PACE")) {
    return null;
  }
  return null;
};

const pushStep = (segments, step, sport) => {
  const repeats = Math.max(1, Number(step?.repeat_count) || 1);
  const role = roleFor(step?.step_type);
  const seconds = Math.max(1, Number(step?.duration_seconds) || 30);
  const fromIntensity = numericHeight(step?.intensity, sport);
  const height = fromIntensity == null ? ROLE_HEIGHT[role] : fromIntensity;
  for (let index = 0; index < repeats; index += 1) {
    segments.push({
      role,
      seconds,
      height,
      color: ROLE_COLOR[role],
    });
    (step?.substeps || []).forEach((substep) => pushStep(segments, substep, sport));
  }
};

export const buildWorkoutProfile = (template) => {
  const segments = [];
  const sport = template?.sport;
  ["warmup", "main_set", "cooldown"].forEach((key) => {
    (template?.[key] || []).forEach((step) => pushStep(segments, step, sport));
  });
  const total = segments.reduce((sum, segment) => sum + segment.seconds, 0) || 1;
  return segments.map((segment) => ({
    ...segment,
    width: segment.seconds / total,
  }));
};

export const CATEGORY_LABELS = {
  ACTIVE_RECOVERY: "Active Recovery",
  ENDURANCE: "Endurance",
  TEMPO: "Tempo",
  SWEET_SPOT: "Sweet Spot",
  THRESHOLD: "Threshold",
  OVER_UNDERS: "Over-Unders",
  VO2_MAX: "VO2 Max",
  ANAEROBIC_CAPACITY: "Anaerobic Capacity",
  SPRINT: "Sprint",
  CLIMBING_STRENGTH_ENDURANCE: "Climbing / Strength Endurance",
  FTP_TEST: "FTP Test / Assessment",
  OPENERS_TAPER: "Openers / Taper",
  RECOVERY_RUN: "Recovery Run",
  EASY_RUN: "Easy Run",
  LONG_RUN: "Long Run",
  TEMPO_RUN: "Tempo Run",
  THRESHOLD_INTERVALS: "Threshold Intervals",
  SPEED_VO2_INTERVALS: "Speed / VO2 Intervals",
  FARTLEK: "Fartlek",
  HILL_REPEATS: "Hill Repeats",
  PROGRESSION_RUN: "Progression Run",
  STRIDES: "Strides",
  RACE_PREP: "Race Prep",
};

export const categoryLabel = (template) => (
  CATEGORY_LABELS[template?.athlete_facing_category] || "Workout"
);

export const progressionLabel = (level) => {
  const text = String(level || "FOUNDATIONAL").toLowerCase().replaceAll("_", " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const parseFriendlyDuration = (value) => {
  const text = String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!text) return { minutes: null };
  const hourMinute = text.match(/^(\d+)\s*h(?:r|rs|our|ours)?\s*(\d+)\s*m(?:in(?:ute)?s?)?$/);
  const hours = text.match(/^(\d+)\s*h(?:r|rs|our|ours)?$/);
  const minutes = text.match(/^(\d+)\s*m(?:in(?:ute)?s?)?$/);
  const bare = text.match(/^(\d+)$/);
  const match = hourMinute || hours || minutes || bare;
  if (!match) {
    return { error: "Enter a duration such as 45 min, 1h 30m, or 3h." };
  }
  const parsed = hourMinute
    ? (Number(hourMinute[1]) * 60) + Number(hourMinute[2])
    : hours
      ? Number(hours[1]) * 60
      : Number((minutes || bare)[1]);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return { error: "Enter a duration greater than 0." };
  }
  return { minutes: parsed };
};

export const supportsRequestedDuration = (template, minutes) => {
  if (minutes == null) return true;
  const min = template?.duration?.minutes_min;
  const max = template?.duration?.minutes_max;
  if (min != null && minutes < min) return false;
  if (max != null && minutes > max) return false;
  return true;
};

export const durationLabel = (template) => {
  const min = template?.duration?.minutes_min;
  const max = template?.duration?.minutes_max;
  if (min != null && max != null) return `${min}–${max} min`;
  if (min != null) return `${min}+ min`;
  if (max != null) return `Up to ${max} min`;
  return "Flexible duration";
};

const PURPOSE_COPY = {
  RECOVERY: "Very easy effort so you can absorb training.",
  AEROBIC_BASE: "Steady aerobic work you can talk through.",
  ENDURANCE: "Longer steady work that builds staying power.",
  TEMPO: "Comfortably hard work, still under control.",
  MUSCULAR_ENDURANCE: "Sustained strength at a controlled effort.",
  THRESHOLD: "Hard work you can just about hold.",
  VO2: "Short hard efforts with full recoveries.",
  ANAEROBIC: "Brief very hard repeats.",
  SPRINT: "Short fast efforts with complete recovery.",
  FATIGUE_RESISTANCE: "Quality work after you are already tired.",
  TECHNIQUE: "Smooth, quick movement at an easy effort.",
  SKILL: "Practice smooth form without going hard.",
  PACE_CONTROL: "Learn to change pace on purpose.",
  EVENT_SPECIFIC: "Practice the effort you want on race day.",
  ASSESSMENT: "A controlled test of your current fitness.",
  TAPER: "Short openers that leave you feeling fresh.",
};

export const purposeCopy = (template) => (
  PURPOSE_COPY[template?.primary_purpose] || "A structured session from the shared library."
);

const CYCLING_UNITS = new Set(["WATTS", "FTP_PERCENT", "BPM", "HR_PERCENT", "HR_ZONE", "RPE_0_10", "CADENCE_RPM", "NONE"]);
const RUNNING_UNITS = new Set(["PACE_SEC_PER_KM", "PACE_SEC_PER_MILE", "BPM", "HR_PERCENT", "HR_ZONE", "RPE_0_10", "CADENCE_RPM", "WATTS", "NONE"]);

export const cloneWorkoutSections = (template) => ({
  warmup: JSON.parse(JSON.stringify(template?.warmup || [])),
  main_set: JSON.parse(JSON.stringify(template?.main_set || [])),
  cooldown: JSON.parse(JSON.stringify(template?.cooldown || [])),
});

const stepSeconds = (step) => {
  const own = Number(step?.duration_seconds) || 0;
  const nested = (step?.substeps || []).reduce((sum, child) => sum + stepSeconds(child), 0);
  return (own + nested) * Math.max(1, Number(step?.repeat_count) || 1);
};

export const workoutTotalSeconds = (sections) => (
  ["warmup", "main_set", "cooldown"].reduce(
    (sum, key) => sum + (sections?.[key] || []).reduce((inner, step) => inner + stepSeconds(step), 0),
    0,
  )
);

export const formatWorkoutClock = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remain = total % 60;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}:${String(remain).padStart(2, "0")}`;
};

const walkSteps = (steps, visit) => {
  (steps || []).forEach((step) => {
    visit(step);
    walkSteps(step.substeps, visit);
  });
};

export const validateEditedSections = (sections, sport) => {
  const errors = [];
  const allowed = sport === "running" ? RUNNING_UNITS : CYCLING_UNITS;
  ["warmup", "main_set", "cooldown"].forEach((key) => {
    walkSteps(sections?.[key], (step) => {
      if (step.duration_seconds != null && Number(step.duration_seconds) <= 0) {
        errors.push("Duration must be greater than 0.");
      }
      if (Number(step.repeat_count) < 1) {
        errors.push("Repeat count must be at least 1.");
      }
      [step.intensity, step.cadence_target].forEach((target) => {
        if (target?.unit && !allowed.has(String(target.unit).toUpperCase())) {
          errors.push(`${target.unit} is not a valid target for this sport.`);
        }
      });
    });
  });
  return [...new Set(errors)];
};

const setAtPath = (sections, path, patch) => {
  const next = JSON.parse(JSON.stringify(sections));
  let cursor = next[path[0]];
  for (let index = 1; index < path.length - 1; index += 1) {
    cursor = cursor[path[index]];
  }
  const last = path[path.length - 1];
  cursor[last] = { ...cursor[last], ...patch };
  return next;
};

export const updateEditedStep = setAtPath;

export const environmentLabel = (template) => {
  const envs = template?.supported_environments || [];
  const indoor = envs.some((item) => item === "INDOOR_TRAINER" || item === "TREADMILL");
  const outdoor = envs.includes("OUTDOOR");
  if (indoor && outdoor) return "Indoor or outdoor";
  if (indoor) return "Indoor";
  if (outdoor) return "Outdoor";
  return "Any setting";
};
